// Libs
import classNames from 'classnames/bind';
import { Controller, useForm } from 'react-hook-form';
import { Box, Button, DialogActions, Grid2, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
// Components, Layouts, Pages
import { BaseDatePicker, BaseInput, BaseSelect, Loading } from '~/components/common';
// Others
import useDebounce from '~/utils/hooks/useDebounce';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IQueryParamBase } from '~/utils/interface/common';
import { IEmployee } from '~/utils/interface/employee';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { getFullName } from '~/utils/helper/common';
import { getEmployeeList } from '~/thunks/employee/employeeThunk';
import { IFormJobTask } from '~/utils/interface/job';
import { getTaskDetail } from '~/thunks/task/taskThunk';
import { defaultValuesForm, schema } from './helper';
import { DateFormatEnum, StatusEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './FormCreateTask.module.scss';

type Props = {
  taskId?: string;
  customerId?: string;
  onClose: () => void;
  onSave: (data: IFormJobTask) => void;
};

const cx = classNames.bind(styles);

const FormCreateTask = (props: Props) => {
  //#region Destructuring Props
  const { taskId, onClose, onSave } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/job', 'common/base']);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm<IFormJobTask>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [employeeList, setEmployeeList] = useState<IEmployee[]>([]);
  const [employeeSearchKey, setEmployeeSearchKey] = useState<string>(EMPTY_STRING);
  const debounceEmployeeSearchKey = useDebounce(employeeSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [isLoadingEmployee, setIsLoadingEmployee] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  const handleGetDetailTask = useCallback(
    (taskId: string) => {
      setIsLoading(true);
      dispatch(getTaskDetail(taskId))
        .unwrap()
        .then((res) => {
          const taskDetail = res?.data;

          const newData: IFormJobTask = {
            title: taskDetail?.title || EMPTY_STRING,
            accountId: taskDetail?.assignee?.id || EMPTY_STRING,
            assignee: taskDetail.assignee ? taskDetail.assignee : undefined,
            dueDate: taskDetail?.dueDate
              ? moment(taskDetail.dueDate).format(DateFormatEnum.YYYY_MM_DD)
              : EMPTY_STRING,
            description: taskDetail?.description || EMPTY_STRING,
          };

          reset(newData);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch, reset]
  );

  const handleGetEmployeeList = useCallback(
    (params: IQueryParamBase) => {
      setIsLoadingEmployee(true);

      dispatch(getEmployeeList(params))
        .unwrap()
        .then((res) => {
          if (!res?.data) return;
          const { responses } = res?.data;

          setEmployeeList(responses);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoadingEmployee(false);
        });
    },
    [dispatch]
  );

  //#region Implement Hook
  useEffect(() => {
    if (!taskId) return;

    handleGetDetailTask(taskId);
  }, [taskId, handleGetDetailTask]);

  useEffect(() => {
    const params: IQueryParamBase = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      status: StatusEnum.ACTIVE,
      ...(debounceEmployeeSearchKey && { textSearch: debounceEmployeeSearchKey }),
    };

    handleGetEmployeeList(params);
  }, [debounceEmployeeSearchKey, handleGetEmployeeList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCancelModalFormTask = () => {
    reset(defaultValuesForm);
    onClose();
  };

  const handleSearchEmployeeChange = (searchKey: string) => {
    setEmployeeSearchKey(searchKey);
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} className={cx('form')}>
      <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
        <Grid2 container size={12} columnSpacing={'12px'}>
          <Grid2 size={12}>
            <Controller
              name='title'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  label={t('form_task_task_name_label')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors?.title?.message}
                  required
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name='assignee'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseSelect
                  label={t('form_task_assignee_label')}
                  value={value?.id}
                  onChange={(data) => {
                    const { label, value, ...restData } = data;
                    onChange(restData);
                    setValue('accountId', restData.id, { shouldDirty: true, shouldValidate: true });
                  }}
                  options={
                    employeeList?.map((employee) => ({
                      ...employee,
                      label: getFullName({ ...employee }),
                      value: employee.id,
                    })) || []
                  }
                  onSearchChange={handleSearchEmployeeChange}
                  isLoading={isLoadingEmployee}
                  errorMessage={errors?.accountId?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name='dueDate'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseDatePicker
                  label={t('form_task_due_date_label')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors?.dueDate?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              name='description'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  multiline
                  height={'auto'}
                  rows={4}
                  label={t('form_task_description_label')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors?.description?.message}
                />
              )}
            />
          </Grid2>
        </Grid2>
      </Grid2>

      <DialogActions>
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'flex-end'}
          component={'div'}
          spacing={'12px'}
          className={cx('formFooter')}
        >
          <Button variant='outlined' onClick={handleCancelModalFormTask}>
            {t('common_action_cancel', { ns: 'common/base' })}
          </Button>

          <Button
            variant='contained'
            type='button'
            onClick={handleSubmit(onSave)}
            disabled={taskId ? !isDirty : false}
          >
            {t('common_action_save', { ns: 'common/base' })}
          </Button>
        </Stack>
      </DialogActions>

      {isLoading && <Loading />}
    </Box>
  );
};

export default FormCreateTask;
